import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { SectionTitle, SectionSubtitle } from '../../components/ui/Layout';
import { Table, TableWrap } from '../../components/ui/Table';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { Field, Label, Input, HelperText } from '../../components/ui/FormControls';
import { Spinner, ErrorMessage, SuccessMessage } from '../../components/ui/Feedback';
import { api, assetUrl, API_URL } from '../../api/client';
import { useAuth } from '../../context/AuthContext';

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const Thumb = styled.img`
  width: 120px;
  height: 56px;
  object-fit: cover;
  border-radius: 2px;
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const Preview = styled.img`
  max-width: 320px;
  max-height: 160px;
  object-fit: cover;
  border: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: 1rem;
`;

const OrdenInput = styled(Input)`
  width: 80px;
  min-height: 36px;
  padding: 0.3em 0.6em;
`;

function AdminBanners() {
  const { token } = useAuth();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [orden, setOrden] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const loadBanners = () => {
    setLoading(true);
    api
      .get('/api/admin/banners', { token })
      .then(setBanners)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(loadBanners, [token]);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      setError('Selecciona una imagen primero.');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('imagen', file);
    formData.append('orden', String(orden));

    try {
      const response = await fetch(`${API_URL}/api/admin/banners`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error((data && data.error) || 'Error al subir la imagen.');
      }
      setSuccess('Imagen subida. Ya aparece en el carrusel del home.');
      setFile(null);
      setOrden(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadBanners();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleToggle = async (banner) => {
    try {
      await api.patch(`/api/admin/banners/${banner.id}`, { activo: !banner.activo }, { token });
      loadBanners();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleOrdenChange = async (banner, value) => {
    try {
      await api.patch(`/api/admin/banners/${banner.id}`, { orden: Number(value) || 0 }, { token });
      loadBanners();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (banner) => {
    if (!window.confirm('¿Eliminar esta imagen del carrusel?')) return;
    try {
      await api.delete(`/api/admin/banners/${banner.id}`, { token });
      loadBanners();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div>
      <Toolbar>
        <div>
          <SectionTitle $align="left">Banners</SectionTitle>
          <SectionSubtitle $align="left">
            Sube las fotos que rotan en la portada. Solo imágenes: sin textos ni botones.
          </SectionSubtitle>
        </div>
      </Toolbar>

      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
      {success && <SuccessMessage role="status">{success}</SuccessMessage>}

      <Card as="form" onSubmit={handleUpload} style={{ marginBottom: '2rem' }}>
        <Field>
          <Label htmlFor="imagen">Imagen (JPG, PNG, WebP o GIF, máx. 8 MB)</Label>
          <Input
            id="imagen"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            ref={fileInputRef}
            onChange={(e) => setFile(e.target.files[0] || null)}
          />
          <HelperText>Recomendado: fotos horizontales de al menos 1600px de ancho.</HelperText>
        </Field>

        {previewUrl && <Preview src={previewUrl} alt="Vista previa de la imagen a subir" />}

        <Field>
          <Label htmlFor="orden">Orden (menor número aparece primero)</Label>
          <OrdenInput
            id="orden"
            type="number"
            value={orden}
            onChange={(e) => setOrden(e.target.value)}
          />
        </Field>

        <Button type="submit" disabled={uploading || !file}>
          {uploading ? <Spinner $size="18px" /> : 'Subir imagen'}
        </Button>
      </Card>

      {loading ? (
        <Spinner $size="28px" />
      ) : (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Imagen</th>
                <th>Orden</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {banners.map((b) => (
                <tr key={b.id}>
                  <td>
                    <Thumb src={assetUrl(b.imagen_url)} alt={`Banner ${b.id}`} />
                  </td>
                  <td>
                    <OrdenInput
                      type="number"
                      defaultValue={b.orden}
                      aria-label={`Orden del banner ${b.id}`}
                      onBlur={(e) => {
                        if (Number(e.target.value) !== b.orden) {
                          handleOrdenChange(b, e.target.value);
                        }
                      }}
                    />
                  </td>
                  <td>
                    <Badge $status={b.activo ? 'confirmada' : 'cancelada'}>
                      {b.activo ? 'Visible' : 'Oculto'}
                    </Badge>
                  </td>
                  <td>
                    <Actions>
                      <Button $variant="ghost" onClick={() => handleToggle(b)}>
                        {b.activo ? 'Ocultar' : 'Mostrar'}
                      </Button>
                      <Button $variant="danger" onClick={() => handleDelete(b)}>
                        Eliminar
                      </Button>
                    </Actions>
                  </td>
                </tr>
              ))}
              {banners.length === 0 && (
                <tr>
                  <td colSpan={4}>Aún no hay imágenes. Sube la primera arriba.</td>
                </tr>
              )}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </div>
  );
}

export default AdminBanners;
